import { lazy, Suspense, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ImpersonationProvider, useImpersonation } from "@/contexts/ImpersonationContext";
import { useCanAccessMenu, useMenuPermissions, type MenuKey } from "@/hooks/use-menu-permissions";
import { useOwnsLeadList, withLeadListOwnership } from "@/hooks/use-lead-list-ownership";
import { useRealtimeListeners } from "@/lib/realtime";
import { resolveLandingPath } from "@/lib/landing-page";
import { failOpen, kanNavigere, rbacTilstand } from "@/lib/rbac-tilstand";
import { PresenceProvider } from "@/contexts/PresenceContext";
import { TimerProvider } from "@/contexts/TimerContext";
import { CommandPalette } from "@/components/shared/CommandPalette";
import { SrIconSprite } from "@/components/ui/SrIconSprite";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import SalesDashboardPage from "@/pages/SalesDashboardPage";
import ReportsPage from "@/pages/ReportsPage";
import SalesDisplayPage from "@/pages/SalesDisplayPage";
import SalesDisplayDraftPage from "@/pages/SalesDisplayDraftPage";
import ProjectsPage from "@/pages/ProjectsPage";
import NewProjectPage from "@/pages/NewProjectPage";
import ProjectDetailLayout from "@/pages/project-detail/ProjectDetailLayout";
import OverviewPage from "@/pages/project-detail/OverviewPage";
import BudgetPage from "@/pages/project-detail/BudgetPage";
import ApprovalPage from "@/pages/project-detail/ApprovalPage";
import AdminTimerPage from "@/pages/project-detail/AdminTimerPage";
import EconomyPage from "@/pages/project-detail/EconomyPage";
import DocumentsPage from "@/pages/project-detail/DocumentsPage";
import EquipmentTabPage from "@/pages/project-detail/EquipmentTabPage";
import LocationsTabPage from "@/pages/project-detail/LocationsTabPage";
import AdminPage from "@/pages/project-detail/AdminPage";
import LogPage from "@/pages/project-detail/LogPage";
import InvoicePage from "@/pages/InvoicePage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import DisplayPage from "@/pages/DisplayPage";
import DisplayProductionPage from "@/pages/DisplayProductionPage";
import DisplayKantinePage from "@/pages/DisplayKantinePage";
// RoomBookingPage removed (VIB-347)
import OrganisasjonSettings from "@/pages/settings/OrganisasjonSettings";
import TilgangerSettings from "@/pages/settings/TilgangerSettings";
import BrukereSettings from "@/pages/settings/BrukereSettings";
import OkonomiSettings from "@/pages/settings/OkonomiSettings";
import IntegrasjonerSettings from "@/pages/settings/IntegrasjonerSettings";
import WidgetsSettings from "@/pages/settings/WidgetsSettings";
import DriftSettings from "@/pages/settings/DriftSettings";
import FeedbackAdminPage from "@/pages/FeedbackAdminPage";
import MineTilbakemeldingerPage from "@/pages/MineTilbakemeldingerPage";
import OffersPage from "@/pages/OffersPage";
import OfferEditPage from "@/pages/OfferEditPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import MeetingSummaryPage from "@/pages/MeetingSummaryPage";
import CustomersPage from "@/pages/CustomersPage";
import FreelancersPage from "@/pages/FreelancersPage";
import ChatPage from "@/pages/ChatPage";
import FreelancerDetailPage from "@/pages/FreelancerDetailPage";
import FreelanceSuppliersPage from "@/pages/FreelanceSuppliersPage";
import AgreementsPage from "@/pages/AgreementsPage";
import EquipmentPage from "@/pages/EquipmentPage";
import EquipmentDetailPage from "@/pages/EquipmentDetailPage";
import EquipmentInsurancePage from "@/pages/EquipmentInsurancePage";
import EquipmentProductDataPage from "@/pages/EquipmentProductDataPage";
import EquipmentOccupancyPage from "@/pages/EquipmentOccupancyPage";
import PackingListPage from "@/pages/PackingListPage";
import EquipmentServicePage from "@/pages/EquipmentServicePage";
import SalgsmuligheterPage from "@/pages/SalgsmuligheterPage";
import TimeRegistrationPage from "@/pages/TimeRegistrationPage";
import VarslerPage from "@/pages/VarslerPage";
import TimebankApprovalsPage from "@/pages/TimebankApprovalsPage";
import CloseMonthPage from "@/pages/CloseMonthPage";
import ProductCatalogPage from "@/pages/ProductCatalogPage";
import AdminImportPage from "@/pages/AdminImportPage";
import ChecklistTemplatesPage from "@/pages/ChecklistTemplatesPage";
import LaeringPage from "@/pages/LaeringPage";
import StyleGuidePage from "@/pages/StyleGuidePage";
import ExtraFormPage from "@/pages/ExtraFormPage";
import StatisterPrintPage from "@/pages/StatisterPrintPage";
import NotFound from "@/pages/NotFound";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { EnvironmentBanner } from "@/components/shared/EnvironmentBanner";

const HjelpLayout = lazy(() => import("@/components/hjelp/HjelpLayout"));
// VIB-2938: lokasjonssidene lastes for seg, så Google Maps-biblioteket ikke havner i hovedpakken.
const LocationsPage = lazy(() => import("@/pages/LocationsPage"));
const LocationDetailPage = lazy(() => import("@/pages/LocationDetailPage"));
const HjelpOversikt = lazy(() =>
  import("@/components/hjelp/HjelpOversikt").then((m) => ({ default: m.HjelpOversikt })),
);
const DokumenterView = lazy(() =>
  import("@/components/hjelp/dokumenter/DokumenterView").then((m) => ({
    default: m.DokumenterView,
  })),
);
const HjelpTemaView = lazy(() =>
  import("@/components/hjelp/HjelpTemaView").then((m) => ({ default: m.HjelpTemaView })),
);
const HjelpVeiledninger = lazy(() =>
  import("@/components/hjelp/HjelpVeiledninger").then((m) => ({ default: m.HjelpVeiledninger })),
);
const HjelpNytt = lazy(() =>
  import("@/components/hjelp/HjelpNytt").then((m) => ({ default: m.HjelpNytt })),
);


// VIB-754: 60s default staleTime + refetchOnWindowFocus gjør at brukere
// ser kollegaers endringer uten å reloade siden manuelt. Hooks med lengre
// eksplisitt staleTime (5 min for sales_units, customers, etc.) er uberørt
// — deres data endrer seg sjelden og trenger ikke aggressiv re-fetch.
// VIB-758 sørger for umiddelbar invalidation etter egne mutations, så
// staleTime påvirker primært cross-user-synlighet.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,            // 1 min default — overridd per hook ved behov
      gcTime: 10 * 60 * 1000,          // 10 min garbage collection
      refetchOnWindowFocus: true,      // Refetch når brukeren tabber tilbake til Sølvrev
      refetchOnReconnect: true,        // Refetch ved gjenopprettet nettverk
      retry: 1,
    },
  },
});

/**
 * VIB-675: lett auth-guard for print-sidene (statister, pakkeliste). Vi vil
 * ikke wrap'e dem i AppLayout (sidebar/header), men brukeren må fortsatt være innlogget.
 */
function PrintAuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Skjermflater: rendres UTEN AppLayout, altså uten sidemeny og toppbar.
 *
 * To grunner. (1) En TV skal vise innholdet, ikke appens navigasjon — og en
 * beskjed-slide dekker uansett hele skjermen (`absolute inset-0` mot
 * viewporten), så inne i AppLayout ville den bare dekket innholdsfeltet ved
 * siden av menyen. (2) Previewen i slideoppsettet laster den samme ruta, så
 * det man ser der er det skjermen faktisk viser.
 *
 * Krever innlogging OG `display`-tilgang, samme nøkkel som `/display/salg` inne
 * i appen. At tallene vises åpent på en vegg i resepsjonen er ikke et argument
 * for at hvem som helst skal kunne hente dem opp i sin egen nettleser: uten
 * rollekravet kunne en ekstern regnskapsfører åpne adressen og få BF,
 * omsetning, selgerrangering og bursdager i en ferdig flate. TV-en trenger ikke
 * at kravet fjernes, den trenger en konto som har `display`.
 *
 * Fail-closed mens tilgangene laster: `useCanAccessMenu` returnerer false til
 * dataene er der. Her er det riktig vei å feile, i motsetning til menyen inne i
 * appen, som feiler åpent for å unngå blink av blokkering.
 */
function ScreenAuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const canSeeDisplay = useCanAccessMenu("display");
  if (isLoading) return null;
  if (!user) return <LoginPage />;
  if (!canSeeDisplay) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center">
        <p className="text-muted-foreground">
          Denne kontoen har ikke tilgang til skjermvisningen. Skjermen må være logget
          inn med en konto som har «Display» i menyen.
        </p>
      </div>
    );
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function AuthGate() {
  const { user, isLoading } = useAuth();
  const { activeUser } = useImpersonation();
  // Per-menyKey RBAC fra role_menu_permissions-tabellen. Erstatter
  // tidligere hardkodede {isAdmin && <Route />}-mønstre. Fail-open under
  // loading for å unngå flash av blokkering. NB: hook må kalles før
  // early returns for å overholde rules-of-hooks.
  // VIB-2771: isPending, IKKE isLoading. I React Query v5 er
  // `isLoading === isPending && isFetching`, så isLoading er false i vinduet
  // der query-en er montert men henting ikke er startet — og der manglet
  // dataene, slik at tilgangssjekken falt til fail-CLOSED. Regelen bor nå i
  // `rbac-tilstand.ts`, der den er testbar.
  const { data: menuPerms, isPending: menuPermsPending, isError: menuPermsError } = useMenuPermissions();
  const ownsLeadList = useOwnsLeadList();

  // VIB-755: starter realtime-subscriptions på kjernedata (projects,
  // budget_lines, time_entries) når en bruker er innlogget. Subscriptions
  // ryddes automatisk ved logout via enabled=false.
  useRealtimeListeners(!!user);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  // Route-synlighet følger aktiv (impersonated) bruker, ikke ekte innlogget.
  const activeRole = activeUser?.role ?? user.role;

  const rbac = rbacTilstand({
    isPending: menuPermsPending,
    isError: menuPermsError,
    harData: Boolean(menuPerms),
  });

  const canAccess = (menuKey: MenuKey): boolean => {
    // MEDIUM-13: fail-OPEN kun mens permissions laster (unngå flash av blokkering).
    // Ved hard query-/nettverksfeil eller manglende data etter lasting:
    // fail-CLOSED, så en feilet RBAC-fetch ikke gir alle full tilgang.
    if (failOpen(rbac)) return true;
    if (rbac === "feilet" || !menuPerms) return false;
    return withLeadListOwnership(
      menuKey,
      menuPerms.find((p) => p.role === activeRole && p.menu_key === menuKey)?.can_access ?? false,
      ownsLeadList,
    );
  };

  return (
    <>
      <CommandPalette />
      <AppLayout>
        <Routes>
          <Route
          path="/"
          element={
            canAccess("dashboard") ? (
              <ErrorBoundary>
                <DashboardPage />
              </ErrorBoundary>
            ) : kanNavigere(rbac) ? (
              <Navigate to={resolveLandingPath(canAccess)} replace />
            ) : (
              // VIB-2771: navigasjonen herfra er en `replace` og dermed
              // ugjenkallelig — ruta avmonteres, så den revurderes aldri når
              // tilgangene lander et øyeblikk senere. Vi venter heller enn å
              // gjette på hvor brukeren hører hjemme.
              <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            )
          }
        />
        {/* Salgsdashboard (Revehiet). RBAC via egen menu_key (VIB-1107);
            default synlig for alle interne roller, styrbart i Roller & Tilganger. */}
        {canAccess("salgsdashboard") && (
          <Route
            path="/dashboard/salg"
            element={
              <ErrorBoundary>
                <SalesDashboardPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("rapporter") && (
          <Route
            path="/rapporter"
            element={
              <ErrorBoundary>
                <ReportsPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("prosjekter") && (
          <Route
            path="/prosjekter"
            element={
              <ErrorBoundary>
                <ProjectsPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("prosjekter") && (
          <Route
            path="/prosjekter/ny"
            element={
              <ErrorBoundary>
                <NewProjectPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("prosjekter") && (
          <Route
            path="/prosjekter/:id"
            element={
              <ErrorBoundary>
                <ProjectDetailLayout />
              </ErrorBoundary>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="budsjett" element={<BudgetPage />} />
            <Route path="admin-timer" element={<AdminTimerPage />} />
            <Route path="godkjenne" element={<ApprovalPage />} />
            <Route path="okonomi" element={<EconomyPage />} />
            <Route path="dokumenter" element={<DocumentsPage />} />
            <Route path="utstyr" element={<EquipmentTabPage />} />
            <Route path="lokasjoner" element={<LocationsTabPage />} />
            <Route path="logg" element={<LogPage />} />
            <Route path="administrasjon" element={<AdminPage />} />
          </Route>
        )}
        {canAccess("fakturering") && (
          <Route
            path="/faktura"
            element={
              <ErrorBoundary>
                <InvoicePage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-1290: kundeliste. Dark launch — menynøkkel 'kunder' er PÅ kun
            for superadmin til bølge 7 er verifisert. Kundekortet (/kunde/:id)
            forblir ikke-gated (åpnes fra prosjekter), men beriket her. */}
        {canAccess("kunder") && (
          <Route
            path="/kunder"
            element={
              <ErrorBoundary>
                <CustomersPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-2925: frilanserbanken. Dark launch — menynøkkel 'frilansere' er PÅ
            kun for superadmin til Erik ruller den ut under Tilganger & roller. */}
        {canAccess("frilansere") && (
          <>
            <Route
              path="/frilansere"
              element={
                <ErrorBoundary>
                  <FreelancersPage />
                </ErrorBoundary>
              }
            />
            {/* VIB-2927: siden sjekker i tillegg flagget feature_freelancer_projects. */}
            <Route
              path="/frilansere/leverandorer"
              element={
                <ErrorBoundary>
                  <FreelanceSuppliersPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/frilansere/:id"
              element={
                <ErrorBoundary>
                  <FreelancerDetailPage />
                </ErrorBoundary>
              }
            />
          </>
        )}
        {/* CRM fase 2: kø og pipe. Dark launch: menynøkkel 'salgsmuligheter'
            er PÅ kun for superadmin, og siden sjekker i tillegg flagget
            feature_crm_ko_pipe. */}
        {(canAccess("salgsmuligheter") || canAccess("salgsmuligheter.leads") || canAccess("salgsmuligheter.leadlister")) && (
          <Route
            path="/salgsmuligheter"
            element={
              <ErrorBoundary>
                <SalgsmuligheterPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("salgsmuligheter.leads") && (
          <Route
            path="/salgsmuligheter/leads"
            element={
              <ErrorBoundary>
                <SalgsmuligheterPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-2988: leadlister og ringeaktivitet, egen menynøkkel. */}
        {canAccess("salgsmuligheter.leadlister") && (
          <Route
            path="/salgsmuligheter/leadlister"
            element={
              <ErrorBoundary>
                <SalgsmuligheterPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("salgsmuligheter") && (
          <>
            <Route
              path="/salgsmuligheter/pipe"
              element={
                <ErrorBoundary>
                  <SalgsmuligheterPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/salgsmuligheter/agenter"
              element={
                <ErrorBoundary>
                  <SalgsmuligheterPage />
                </ErrorBoundary>
              }
            />
          </>
        )}
        {/* VIB-1291: løpende avtaler (recurring/rental). Dark launch — menynøkkel
            'avtaler' er PÅ kun for superadmin til bølge 7 er verifisert. */}
        {canAccess("avtaler") && (
          <Route
            path="/avtaler"
            element={
              <ErrorBoundary>
                <AgreementsPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-2938: lokasjonsbanken. Dark launch: menynøkkel 'lokasjoner' er PÅ
            kun for superadmin, og sidene sjekker i tillegg feature_locations. */}
        {canAccess("lokasjoner") && (
          <>
            <Route
              path="/lokasjoner"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<div className="p-8 text-muted-foreground">Laster lokasjoner …</div>}>
                    <LocationsPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route
              path="/lokasjoner/:slug"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<div className="p-8 text-muted-foreground">Laster lokasjoner …</div>}>
                    <LocationDetailPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </>
        )}
        {/* VIB-2917: utstyrsregisteret. Dark launch: menynøkkel 'utstyr' er PÅ
            kun for superadmin, og sidene sjekker i tillegg feature_equipment. */}
        {canAccess("utstyr") && (
          <>
            <Route
              path="/utstyr"
              element={
                <ErrorBoundary>
                  <EquipmentPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/utstyr/forsikring"
              element={
                <ErrorBoundary>
                  <EquipmentInsurancePage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/utstyr/produktdata"
              element={
                <ErrorBoundary>
                  <EquipmentProductDataPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/utstyr/service"
              element={
                <ErrorBoundary>
                  <EquipmentServicePage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/utstyr/belegg"
              element={
                <ErrorBoundary>
                  <EquipmentOccupancyPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/utstyr/:id"
              element={
                <ErrorBoundary>
                  <EquipmentDetailPage />
                </ErrorBoundary>
              }
            />
          </>
        )}
        {canAccess("ansatte") && (
          <Route
            path="/ansatte"
            element={
              <ErrorBoundary>
                <EmployeesPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("ansatte") && (
          <Route
            path="/ansatte/:id"
            element={
              <ErrorBoundary>
                <EmployeeDetailPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-3022: Chat-rom. Siden sjekker i tillegg flagget feature_chat_rom. */}
        {canAccess("chat") && (
          <Route
            path="/chat"
            element={
              <ErrorBoundary>
                <ChatPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("mine-oppgaver") && (
          <Route
            path="/mine-oppgaver"
            element={
              <ErrorBoundary>
                <TimeRegistrationPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("mine-oppgaver") && (
          <Route
            path="/mine-oppgaver/avslutt-maaned"
            element={
              <ErrorBoundary>
                <CloseMonthPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("mine-oppgaver") && (
          <Route
            path="/tidsregistrering"
            element={
              <ErrorBoundary>
                <TimeRegistrationPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.varsler") && (
          <Route
            path="/innstillinger/varsler"
            element={
              <ErrorBoundary>
                <VarslerPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("timebank") && (
          <Route
            path="/timebank"
            element={
              <ErrorBoundary>
                <TimebankApprovalsPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("display") && (
          <Route
            path="/display"
            element={
              <ErrorBoundary>
                <DisplayPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("display") && (
          <Route
            path="/display/produksjon"
            element={
              <ErrorBoundary>
                <DisplayProductionPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("display") && (
          <Route
            path="/display/kantine"
            element={
              <ErrorBoundary>
                <DisplayKantinePage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("display") && (
          <Route
            path="/display/salg"
            element={
              <ErrorBoundary>
                <SalesDisplayPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("tilbud") && (
          <Route
            path="/tilbud"
            element={
              <ErrorBoundary>
                <OffersPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("tilbud") && (
          <Route
            // VIB-2979: valgfri gren i tilbudsarbeidsrommet (/tilbud/:id/grunnlag). Én rute,
            // så siden ikke remonteres og ulagret utkast forsvinner ved grenbytte.
            path="/tilbud/:id/:gren?"
            element={
              <ErrorBoundary>
                <OfferEditPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger") && (
          <Route
            path="/innstillinger"
            // Første underside rollen faktisk har, ikke hardkodet Organisasjon:
            // «innstillinger» er åpen for alle roller fordi Varsler ligger
            // under den, så en hardkodet Organisasjon-redirect sendte enhver
            // rolle inn på admin-flata.
            element={
              // Samme ugjenkallelige replace som på «/» (VIB-2771): skjer den
              // før tilgangene er lest, havner enhver rolle på den første
              // undersiden fail-open tilfeldigvis peker på.
              kanNavigere(rbac) ? (
                <Navigate to={resolveLandingPath(canAccess, ["innstillinger"])} replace />
              ) : (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              )
            }
          />
        )}
        {canAccess("innstillinger.organisasjon") && (
          <Route
            path="/innstillinger/organisasjon"
            element={
              <ErrorBoundary>
                <OrganisasjonSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.tilganger") && (
          <Route
            path="/innstillinger/tilganger"
            element={
              <ErrorBoundary>
                <TilgangerSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.brukere") && (
          <Route
            path="/innstillinger/brukere"
            element={
              <ErrorBoundary>
                <BrukereSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.widgets") && (
          <Route
            path="/innstillinger/widgets"
            element={
              <ErrorBoundary>
                <WidgetsSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.okonomi") && (
          <Route
            path="/innstillinger/okonomi"
            element={
              <ErrorBoundary>
                <OkonomiSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.integrasjoner") && (
          <Route
            path="/innstillinger/integrasjoner"
            element={
              <ErrorBoundary>
                <IntegrasjonerSettings />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger.drift") && (
          <Route
            path="/innstillinger/drift"
            element={
              <ErrorBoundary>
                <DriftSettings />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-680: in-app feedback admin. Gated internt på admin/superadmin via useRBAC */}
        <Route
          path="/feedback"
          element={
            <ErrorBoundary>
              <FeedbackAdminPage />
            </ErrorBoundary>
          }
        />
        {/* Ansatt-side: egne innsendinger + varsel-deep-link (?highlight=<id>) */}
        <Route
          path="/mine-tilbakemeldinger"
          element={
            <ErrorBoundary>
              <MineTilbakemeldingerPage />
            </ErrorBoundary>
          }
        />
        {/* /widget-admin er flyttet inn i /innstillinger som "Widgets"-kort */}
        <Route
          path="/widget-admin"
          element={<Navigate to="/innstillinger#widgets" replace />}
        />
        <Route
          path="/innstillinger/widgets"
          element={<Navigate to="/innstillinger#widgets" replace />}
        />
        {canAccess("innstillinger") && (
          <Route
            path="/innstillinger/produktkatalog"
            element={
              <ErrorBoundary>
                <ProductCatalogPage />
              </ErrorBoundary>
            }
          />
        )}
        {canAccess("innstillinger") && (
          <Route
            path="/admin/import"
            element={
              <ErrorBoundary>
                <AdminImportPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-784: admin redigerer sjekklist-templates for oppstart/status/debrief */}
        <Route
          path="/admin/sjekklister"
          element={
            <ErrorBoundary>
              <ChecklistTemplatesPage />
            </ErrorBoundary>
          }
        />
        {/* VIB-788: læringsbibliotek på tvers av debriefer. LOW-14: meny-
            tilgang styres via RBAC (role_menu_permissions), admin bestemmer. */}
        {canAccess("laering") && (
          <Route
            path="/laering"
            element={
              <ErrorBoundary>
                <LaeringPage />
              </ErrorBoundary>
            }
          />
        )}
        {/* VIB-972: In-app hjelpe-senter — alltid synlig for alle roller (ikke RBAC-gated). */}
        <Route
          path="/hjelp"
          element={
            <ErrorBoundary>
              <Suspense fallback={<div className="p-8 text-muted-foreground">Laster hjelp …</div>}>
                <HjelpLayout />
              </Suspense>
            </ErrorBoundary>
          }
        >
          <Route index element={<HjelpOversikt />} />
          {/* VIB-2621: endringslogg. Statisk segment før :slug, så «nytt» aldri tolkes som tema. */}
          <Route path="nytt" element={<HjelpNytt />} />
          {/*
            VIB-2775: hele veiledningsbiblioteket som egen fane. Må, som «nytt»
            og «arkiv», stå før ":slug" så ruta ikke tolkes som et tema.
          */}
          <Route path="veiledninger" element={<HjelpVeiledninger />} />
          {/*
            VIB-2703: dokumentarkivet. MÅ stå før ":slug", ellers fanger
            tema-ruta «arkiv» som om det var et hjelpe-tema.

            Ruta heter «arkiv», ikke «dokumenter»: det finnes allerede et
            hjelpe-tema med sluggen «dokumenter» (prosjektets vedlegg-fane),
            og en rute her ville skygget for det.
            Tilgangen er RLS-styrt, ikke rute-styrt: alle kan gå hit, men
            ser bare mappene de har lov til. Komponenten rendrer ingenting
            når funksjonsbryteren står av.
          */}
          <Route path="arkiv" element={<DokumenterView />} />
          <Route path=":slug" element={<HjelpTemaView />} />
        </Route>

        <Route
          path="/styleguide"
          element={
            <ErrorBoundary>
              <StyleGuidePage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/kunde/:id"
          element={
            <ErrorBoundary>
              <CustomerDetailPage />
            </ErrorBoundary>
          }
        />
        {/* CRM-4.6: møtereferat. Siden sjekker flagget selv; RLS begrenser transkripsjonen. */}
        <Route
          path="/kunde/:id/moter/:meetingId"
          element={
            <ErrorBoundary>
              <MeetingSummaryPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/moter/:meetingId"
          element={
            <ErrorBoundary>
              <MeetingSummaryPage />
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </AppLayout>
    </>
  );
}

const App = () => (
  <SentryErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <SrIconSprite />
        {/* BrowserRouter er flyttet utenfor AuthProvider slik at AuthContext
            kan bruke useNavigate (VIB-634 PASSWORD_RECOVERY-handler). */}
        <BrowserRouter>
          <AuthProvider>
            <ImpersonationProvider>
              <PresenceProvider>
                <TimerProvider>
                  <Sonner />
                  <EnvironmentBanner />
                  <Routes>
                    {/* Public route: self-service form for extras (no auth required) */}
                    <Route path="/skjema/:token" element={<ExtraFormPage />} />
                    {/* Passordreset-side (VIB-634). Public-rute fordi
                        recovery-tokenet gir midlertidig session. */}
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    {/* VIB-675: print-side rendrer uten AppLayout for ren utskrift.
                        Krever auth, men ikke menu-permissions (vi gjenbruker AuthGate
                        bare for å verifisere innlogging via PrintAuthGate). */}
                    <Route path="/prosjekter/:id/statister/print" element={<PrintAuthGate><StatisterPrintPage /></PrintAuthGate>} />
                    {/* VIB-2920: pakkelisten deles som lenke og skrives ut, uten sidemeny. */}
                    <Route path="/prosjekter/:id/pakkeliste" element={<PrintAuthGate><PackingListPage /></PrintAuthGate>} />
                    {/* Adressen salgsskjermene står på. Menyfri, og den previewen viser. */}
                    <Route
                      path="/skjerm/salg"
                      element={<ScreenAuthGate><SalesDisplayPage /></ScreenAuthGate>}
                    />
                    <Route
                      path="/display/utkast"
                      element={<ScreenAuthGate><SalesDisplayDraftPage /></ScreenAuthGate>}
                    />
                    <Route path="/*" element={<AuthGate />} />
                  </Routes>
                </TimerProvider>
              </PresenceProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
